/** Accords commerciaux — types des réponses et requêtes (module A2). */
import { useQuery } from '@tanstack/react-query';
import type { TonBadge } from '@myron/design-system';
import { get } from '../../../shared/api/client';

export type StatutAccord = 'BROUILLON' | 'EN_VALIDATION' | 'ACTIF' | 'SUSPENDU' | 'EXPIRE' | 'RESILIE' | 'REFUSE';
export type Cible = { type: 'PRODUIT'; produitId: string } | { type: 'SELECTION'; produitIds: string[] } | { type: 'CATEGORIE'; categorieId: string };
export type Segment = 'DETAILLANT' | 'GROSSISTE';

export interface AccordResume {
  id: string; reference: string; intitule: string; statut: StatutAccord; origine: 'NEGOCIATION' | 'DEVIS';
  entreprise: { id: string; raisonSociale: string; reference: string; segment: Segment };
  valideDu: string; valideAu: string | null; cumulPromotions: 'AUCUN' | 'MEILLEUR_PRIX'; nbLignes: number; exceptionsEnAttente: number; creeLe: string; version: number;
}
export interface LigneVue {
  id: string; cible: Cible; cibleVue: { type: Cible['type']; libelle: string; produits: { id: string; sku: string; libelle: string }[] };
  conditionnementType: string; prixHtCentimes: number | null; remiseBp: number | null; quantiteMin: number | null; quantiteMax: number | null;
  exceptionMarge?: { statut: 'A_APPROUVER' | 'APPROUVEE' | 'REFUSEE'; tauxBp: number | null; validationId?: string | null; approuveParNom?: string | null; approuveLe?: string | null } | null;
}
export interface AnalyseLigne {
  ligneId: string; seuilBp: number; pireTauxBp: number | null; sousMinimum: boolean; coutInconnu: boolean;
  produits: { produitId: string; sku: string; libelle: string; prixStandardCentimes: number | null; prixAccordCentimes: number | null; coutRevientCentimes: number | null; tauxBp: number | null; sousMinimum: boolean; coutInconnu: boolean }[];
}
export interface AccordDetail extends AccordResume {
  conditionsParticulieres: string | null; devisId: string | null; creePar: string; soumisPar: string | null; validePar: string | null; valideLe: string | null; motifSuspension: string | null;
  lignes: LigneVue[];
  historique: { date: string; action: string; version: number; motif: string | null; auteur: string; statut: StatutAccord; valideDu: string; valideAu: string | null; lignes: LigneVue[] }[];
  validationsExceptions: { id: string; statut: string; demandeLe: string; traiteLe: string | null; motifRejet: string | null }[];
  conflits: { ligneId: string; avecReference: string; avecLigneId: string; motif: string }[];
  analyseMarges?: AnalyseLigne[];
  actions: ('MODIFIER' | 'SOUMETTRE' | 'VALIDER' | 'REFUSER' | 'SUSPENDRE' | 'REACTIVER' | 'RESILIER')[];
}
export interface Options {
  clients: { id: string; reference: string; raisonSociale: string; segment: Segment }[];
  produits: { id: string; sku: string; libelle: string; categorieId: string; conditionnements: string[] }[];
  categories: { id: string; libelle: string }[];
}
export interface LigneSimulee {
  produitId: string; libelle: string; sku: string; conditionnementType: string; quantite: number; conflit?: string[];
  prixTrouve?: boolean; prixUnitaireHtCentimes?: number | null; prixStandardCentimes?: number | null; prixBarreCentimes?: number | null; promotion?: boolean;
  source?: 'CLIENT' | 'GROUPE' | 'BASE' | null; accordReference?: string | null; quantiteMin?: number | null; quantiteMax?: number | null;
  horsAccord?: { code: 'SOUS_MINIMUM' | 'AU_DELA_MAXIMUM' } | null; totalHtCentimes?: number | null;
  marge?: { tauxBp: number | null; seuilBp: number; sousMinimum: boolean; coutInconnu: boolean };
}
export interface Grossiste {
  id: string; reference: string; raisonSociale: string; statut: string; groupeTarifaire: string | null; accordsActifs: number; accordsEnValidation: number; accordsBrouillon: number;
  prochaineEcheance: string | null; commandes12Mois: number; caHt12MoisCentimes: number; derniereCommande: string | null;
}

export const LIBELLE_STATUT_ACCORD: Record<StatutAccord, string> = {
  BROUILLON: 'Brouillon', EN_VALIDATION: 'En validation', ACTIF: 'Actif', SUSPENDU: 'Suspendu', EXPIRE: 'Expiré', RESILIE: 'Résilié', REFUSE: 'Refusé',
};
export const TON_STATUT_ACCORD: Record<StatutAccord, TonBadge> = {
  BROUILLON: 'sable', EN_VALIDATION: 'alerte', ACTIF: 'succes', SUSPENDU: 'erreur', EXPIRE: 'neutre', RESILIE: 'neutre', REFUSE: 'erreur',
};
export const LIBELLE_ACTION: Record<string, string> = {
  CREATION: 'Création', CREATION_DEPUIS_DEVIS: 'Création depuis un devis', MODIFICATION: 'Modification', SOUMISSION: 'Soumission à validation',
  EXCEPTION_MARGE_APPROUVEE: 'Exception de marge approuvée', VALIDATION: 'Validation — accord actif', ACTIVATION: 'Activation', REFUS: 'Refus',
  SUSPENSION: 'Suspension', REACTIVATION: 'Réactivation', RESILIATION: 'Résiliation', EXPIRATION: 'Expiration automatique',
};
export const LIBELLE_SEGMENT: Record<Segment, string> = { DETAILLANT: 'Détaillant', GROSSISTE: 'Grossiste' };
export const LIBELLE_CUMUL = { AUCUN: 'Le prix de l’accord prime (pas de promotion)', MEILLEUR_PRIX: 'Meilleur prix entre accord et promotion' } as const;

export const useOptionsAccords = () => useQuery({ queryKey: ['admin', 'accords', 'options'], queryFn: () => get<Options>('/admin/accords/options'), staleTime: 60_000 });

export function libellePrix(l: { prixHtCentimes: number | null; remiseBp: number | null }, euros: (c: number) => string) {
  if (l.prixHtCentimes != null) return `${euros(l.prixHtCentimes)} HT net`;
  if (l.remiseBp != null) return `Remise ${(l.remiseBp / 100).toLocaleString('fr-FR')} % sur le tarif standard`;
  return '—';
}
export function libelleQuantites(l: { quantiteMin: number | null; quantiteMax: number | null }) {
  if (l.quantiteMin != null && l.quantiteMax != null) return `De ${l.quantiteMin} à ${l.quantiteMax}`;
  if (l.quantiteMin != null) return `Au moins ${l.quantiteMin}`;
  if (l.quantiteMax != null) return `Au plus ${l.quantiteMax}`;
  return 'Sans limite';
}
